import React, { useCallback } from 'react';
import clsx from 'clsx';
import { useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/pro-light-svg-icons/faPlus';
import { TypedMemo } from '../../../../common/types';
import { ReleasePlanDay } from '../../../../../redux/api/fymAPI.types';
import { PlanPostChip } from './PlanPostChip';
import { planMessages } from './planMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the Plan tab; each part uses its own slice of it */
import styles from './Plan.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface TimelineDayProps {
  day: ReleasePlanDay;
  /** "−14", "Day 0", "Week 1". */
  label: string;
  /** "Thu 2 Oct". */
  dateLabel: string;
  /** "Announce", "Countdown". */
  title: string;
  isPast: boolean;
  isReleaseDay: boolean;
  /** Read only (the closed rollout of a released song): no "Add a draft". */
  readOnly?: boolean;
  isAdding?: boolean;
  /** "Add a draft" of this gap day. */
  onAddDay?: (day: ReleasePlanDay) => void;
  /** Push mode: the tick box of the week (unticked weeks are left out). */
  tick?: { isOn: boolean; label: string; onToggle: (day: ReleasePlanDay) => void };
}

/**
 * One column of the rollout timeline (designs f1, f2): the offset, the date and the title, then the
 * posts of the day, a gap with "Add a draft", or the task line of the Canvas day.
 */
const TimelineDayComponent: React.FC<TimelineDayProps> = ({
  day,
  label,
  dateLabel,
  title,
  isPast,
  isReleaseDay,
  readOnly = false,
  isAdding = false,
  onAddDay,
  tick,
}) => {
  const intl = useIntl();
  const add = useCallback(() => onAddDay?.(day), [day, onAddDay]);
  const toggle = useCallback(() => tick?.onToggle(day), [day, tick]);

  return (
    <li
      className={clsx(styles.day, isPast && styles.dayPast, isReleaseDay && styles.dayZero)}
      data-testid="plan-day"
      data-day={day.key}
    >
      <div className={styles.dayHead}>
        <span className={styles.dayOffset}>{label}</span>
        <span className={styles.dayDate}>{dateLabel}</span>
        <span className={styles.dayTitle}>{title}</span>
        {tick !== undefined && (
          <input
            type="checkbox"
            className={styles.dayTick}
            checked={tick.isOn}
            onChange={toggle}
            aria-label={tick.label}
          />
        )}
      </div>
      {day.isTask && <span className={styles.taskLine}>{intl.formatMessage(planMessages.taskLine)}</span>}
      {day.posts.map((post) => (
        <PlanPostChip key={post.uuid} post={post} />
      ))}
      {day.gap &&
        (readOnly || onAddDay === undefined ? (
          <span className={styles.gap}>{intl.formatMessage(planMessages.legendGap)}</span>
        ) : (
          <button type="button" className={styles.gap} onClick={add} disabled={isAdding}>
            <FontAwesomeIcon icon={faPlus} aria-hidden="true" />
            {intl.formatMessage(planMessages.addADraft)}
          </button>
        ))}
    </li>
  );
};

export const TimelineDay = TypedMemo(TimelineDayComponent);
